import { InteractionSlice, PlannerSliceCreator } from '../types';

/**
 * Transient interaction state: cut/duplicate/drag-resize modes, hover tracking,
 * row/date selection. All setters are identity-guarded (returning the SAME state
 * reference when nothing changes) because they fire from high-frequency pointer
 * events — an unguarded set would re-render every subscribed bar per mousemove.
 */
export const createInteractionSlice: PlannerSliceCreator<InteractionSlice> = set => ({
  activeCutId: null,
  setActiveCutId: (id: number | null) =>
    set(state => (state.activeCutId === id ? state : { activeCutId: id })),

  activeCutFragmentStart: null,
  setActiveCutFragmentStart: (start: string | null) =>
    set(state =>
      state.activeCutFragmentStart === start ? state : { activeCutFragmentStart: start }
    ),

  activeDuplicateId: null,
  setActiveDuplicateId: (id: number | null) =>
    set(state => (state.activeDuplicateId === id ? state : { activeDuplicateId: id })),

  activeDragResizeId: null,
  setActiveDragResizeId: (id: number | null) =>
    set(state => (state.activeDragResizeId === id ? state : { activeDragResizeId: id })),

  interactingAssignmentId: null,
  setInteractingAssignmentId: (id: number | null) =>
    set(state => (state.interactingAssignmentId === id ? state : { interactingAssignmentId: id })),

  isDragResizeInteracting: false,
  setIsDragResizeInteracting: (isInteracting: boolean) =>
    set(state =>
      state.isDragResizeInteracting === isInteracting
        ? state
        : { isDragResizeInteracting: isInteracting }
    ),

  hoveredAssignmentId: null,
  setHoveredAssignmentId: (id: number | null) =>
    set(state => (state.hoveredAssignmentId === id ? state : { hoveredAssignmentId: id })),
  clearHoveredAssignmentIdIf: (id: number) =>
    // Guarded clear: a no-op (returns the same state ref, no re-render) when
    // another bar is already hovered. zustand set is synchronous and not
    // React-batched, so by the time the previous bar's deferred onHide runs,
    // the new bar's id is already in the store — this prevents it from being
    // nulled out and keeps the new bar's lift animation.
    set(state => (state.hoveredAssignmentId === id ? { hoveredAssignmentId: null } : state)),

  hoveredTimeOffId: null,
  setHoveredTimeOffId: (id: number | null) =>
    set(state => (state.hoveredTimeOffId === id ? state : { hoveredTimeOffId: id })),
  clearHoveredTimeOffIdIf: (id: number) =>
    set(state => (state.hoveredTimeOffId === id ? { hoveredTimeOffId: null } : state)),

  selectedRow: null,
  setSelectedRow: (id: string) => set({ selectedRow: id }),
  resetSelectedRow: () => set({ selectedRow: null }),

  selectedDates: {
    startDate: null,
    endDate: null,
  },
  setSelectedDates: ({ startDate, endDate }) => set({ selectedDates: { startDate, endDate } }),
  resetSelectedDates: () => set({ selectedDates: { startDate: null, endDate: null } }),
});
